import {MultiSelect, type MultiSelectProps} from '@mantine/core';
import classNames from 'classnames';
import type React from 'react';
import {forwardRef} from 'react';
import {MdAdd, MdCheck, MdClose} from 'react-icons/md';

export type MultiSelectItem = {
  value: string;
  label: string;
  selected?: boolean;
};

type Props = MultiSelectProps & {
  onSelectedItemsChange: (selectedItems: string[]) => void;
  value: string[];
};

const SelectItem = forwardRef<HTMLDivElement, MultiSelectItem>(
  ({label, selected, ...others}: MultiSelectItem, ref) => (
    <div
      ref={ref}
      {...others}
      className={classNames(
        'flex items-center gap-8 py-4 cursor-pointer text-black-1 hover:bg-background-2 px-8',
        {
          'text-primary font-bold': selected,
        },
      )}
    >
      <MdCheck
        size={16}
        className={classNames('', {
          'text-primary': selected,
          'text-black-5': !selected,
        })}
      />
      <div className="text-base">{label}</div>
    </div>
  ),
);

const ValueTags: React.FC<{
  items: MultiSelectItem[];
  onRemove: (value: string) => void;
}> = ({items, onRemove}) => {
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-8">
      {items.map((item) => (
        <div
          key={item.value}
          className="flex items-center py-4 px-8 bg-primary rounded-full w-fit gap-4"
        >
          <div className="text-white text-sm line-clamp-1">{item.label}</div>
          <MdClose
            className="text-white cursor-pointer"
            size={12}
            onClick={() => {
              onRemove(item.value);
            }}
          />
        </div>
      ))}
    </div>
  );
};

const CustomValueComponent: React.FC<{placeholder: string}> = ({
  placeholder,
}) => {
  return <div className="text-black-3">{placeholder}</div>;
};

export const MultiSelectWrapper: React.FC<Props> = ({
  onSelectedItemsChange,
  value,
  searchable = false,
  ...rest
}) => {
  const selectedItems: MultiSelectItem[] = (
    rest.data as MultiSelectItem[]
  )?.filter((item) => value.includes(item.value));

  const handleRemove = (value: string) => {
    onSelectedItemsChange(
      selectedItems
        .filter((item) => item.value !== value)
        .map((item) => item.value),
    );
  };

  return (
    <div className="flex flex-col gap-16">
      <ValueTags items={selectedItems} onRemove={handleRemove} />
      <MultiSelect
        onChange={onSelectedItemsChange}
        itemComponent={SelectItem}
        valueComponent={(props) =>
          props.index === 0 && !searchable ? (
            <CustomValueComponent
              placeholder={rest.placeholder ?? '選択してください'}
            />
          ) : null
        }
        value={value}
        placeholder={rest.placeholder ?? '選択してください'}
        radius={16}
        disableSelectedItemFiltering
        dropdownPosition="bottom"
        rightSection={
          value.length > 0 ? <MdAdd className="text-black-3" size={20} /> : null
        }
        styles={{rightSection: {pointerEvents: 'none'}}}
        searchable={searchable}
        {...rest}
      />
    </div>
  );
};
